const SHOPPER_AVAILABILITY_STATES = new Set([
  'approved-for-dev-preview',
  'production-approved',
  'catalog',
]);

const CATEGORY_LABELS = {
  animals: 'Animals',
  sanrio: 'Sanrio',
  characters: 'Characters',
  symbols: 'Symbols',
  food: 'Food',
  hawaii: 'Hawai‘i',
  minecraft: 'Minecraft',
  pokemon: 'Pokémon',
  mario: 'Mario',
  letters: 'Letters',
};

const INDIVIDUAL_KEYCAP_LABEL_COLLATOR = new Intl.Collator('en', {
  sensitivity: 'base',
  numeric: true,
});

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[character]);
}

export function filterShopperVisibleKeycaps(keycaps) {
  if (!Array.isArray(keycaps)) return [];
  return keycaps.filter((cap) => {
    if (!cap || typeof cap !== 'object') return false;
    if (!cap.id || !cap.label || !cap.image) return false;
    if (cap.visibility === 'hidden' || cap.visibility === 'disabled') return false;
    const availabilityState = String(cap.intake?.approvalState || cap.intake?.status || 'catalog');
    return SHOPPER_AVAILABILITY_STATES.has(availabilityState);
  });
}

export function filterIndividualKeycaps(keycaps, filters = {}) {
  const searchTerms = String(filters.search || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  const category = normalizeKeycapCategory(filters.category || 'all');
  return keycaps.filter((cap) => {
    if (category !== 'all' && normalizeKeycapCategory(keycapCategoryValue(cap)) !== category) return false;
    if (!searchTerms.length) return true;
    const haystack = [
      cap.id,
      cap.label,
      cap.category,
      cap.intake?.category,
      cap.intake?.notes,
      ...(Array.isArray(cap.tags) ? cap.tags : []),
      ...(Array.isArray(cap.intake?.tags) ? cap.intake.tags : []),
    ].join(' ').toLowerCase();
    return searchTerms.every((term) => haystack.includes(term));
  });
}

export function sortIndividualKeycaps(keycaps) {
  if (!Array.isArray(keycaps)) return [];
  return [...keycaps].sort((a, b) => (
    INDIVIDUAL_KEYCAP_LABEL_COLLATOR.compare(String(a?.label || ''), String(b?.label || ''))
    || INDIVIDUAL_KEYCAP_LABEL_COLLATOR.compare(String(a?.id || ''), String(b?.id || ''))
  ));
}

function normalizeKeycapCategory(value) {
  return String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function keycapCategoryValue(cap) {
  return String(cap?.category || cap?.intake?.category || 'characters').trim();
}

function categoryLabel(id, fallback = '') {
  const source = fallback || id;
  return CATEGORY_LABELS[id] || source.replace(/[-_]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

export function individualKeycapCategorySections(catalog, keycaps) {
  const assigned = new Map();
  keycaps.forEach((cap) => {
    const raw = keycapCategoryValue(cap);
    const id = normalizeKeycapCategory(raw);
    if (id && !assigned.has(id)) assigned.set(id, raw);
  });

  const categoryDefinitions = Array.isArray(catalog?.keycapCategories) ? catalog.keycapCategories : [];
  const archivedCategoryIds = new Set(categoryDefinitions
    .filter((category) => category && (category.archived || category.hidden || category.visibility === 'hidden' || category.status === 'archived'))
    .map((category) => normalizeKeycapCategory(category.id || category.label))
    .filter(Boolean));
  const configured = categoryDefinitions
    .filter((category) => category && !category.archived && !category.hidden && category.visibility !== 'hidden' && category.status !== 'archived')
    .map((category) => {
      const id = normalizeKeycapCategory(category.id || category.label);
      return {
        id,
        label: categoryLabel(id, category.label || category.id),
        sortOrder: Number.isFinite(category.sortOrder) ? Number(category.sortOrder) : 999,
      };
    })
    .filter((category) => category.id && assigned.has(category.id));

  const configuredIds = new Set(configured.map((category) => category.id));
  const extras = [...assigned.entries()]
    .filter(([id]) => !configuredIds.has(id) && !archivedCategoryIds.has(id))
    .map(([id, label]) => ({ id, label: categoryLabel(id, label), sortOrder: 1000 }));

  return [...configured, ...extras]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true }))
    .map(({ sortOrder: _sortOrder, ...category }) => category);
}

export function nextIndividualKeycapDisclosureState(openCategories, sectionId, currentlyOpen) {
  const next = new Set(Array.isArray(openCategories) ? openCategories : []);
  const open = !currentlyOpen;
  if (open) next.add(sectionId);
  else next.delete(sectionId);
  return { open, openCategories: [...next] };
}

export function individualKeycapProperties(cap) {
  if (!cap?.id || !cap?.label) throw new Error('A valid keycap selection is required.');
  return {
    Keycap: String(cap.label),
    _wired808_item_type: 'extra_keycap',
    _wired808_extra_keycap_id: String(cap.id),
    _wired808_extra_keycap_label: String(cap.label),
  };
}

export function individualKeycapPriceCents(cap) {
  const id = String(cap?.id || '').trim().toLowerCase();
  const label = String(cap?.label || '').trim();
  const is3d = id.endsWith('-3d') || /\s[-\u2013\u2014]\s*3d$/i.test(label);
  const priceCents = cap?.individualPriceCents ?? (is3d ? 600 : 500);
  if (!Number.isSafeInteger(priceCents) || priceCents <= 0) {
    throw new Error('Individual keycap price must be a positive whole number of cents.');
  }
  if (is3d && priceCents !== 600) {
    throw new Error('Individual 3D keycaps must use the 600-cent price tier.');
  }
  return priceCents;
}

function normalizeVariantPriceCents(value) {
  if (Number.isSafeInteger(value) && value > 0) return value;
  if (typeof value === 'string' && /^\d+(?:\.\d{1,2})?$/.test(value.trim())) {
    const cents = Math.round(Number(value) * 100);
    if (Number.isSafeInteger(cents) && cents > 0) return cents;
  }
  return null;
}

export function resolveIndividualKeycapVariant(variants, priceCents) {
  if (!Array.isArray(variants) || !Number.isSafeInteger(priceCents) || priceCents <= 0) return null;
  const matches = variants.filter((variant) => {
    if (!variant || variant.available !== true) return false;
    if (!/^\d+$/.test(String(variant.id || ''))) return false;
    return normalizeVariantPriceCents(variant.price) === priceCents;
  });
  if (matches.length !== 1) return null;
  return { id: String(matches[0].id), priceCents };
}

function formatIndividualKeycapAmount(priceCents) {
  return `$${(priceCents / 100).toFixed(2)}`;
}

function formatIndividualKeycapPrice(priceCents) {
  return `${formatIndividualKeycapAmount(priceCents)} USD`;
}

export function normalizeIndividualKeycapQuantity(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.min(10, Math.max(1, Math.trunc(numeric)));
}

export function individualKeycapPurchaseSummary(unitPriceCents, quantity) {
  if (!Number.isSafeInteger(unitPriceCents) || unitPriceCents <= 0) {
    throw new Error('Individual keycap price must be a positive whole number of cents.');
  }
  const normalizedQuantity = normalizeIndividualKeycapQuantity(quantity);
  const totalPriceCents = unitPriceCents * normalizedQuantity;
  return {
    unitPriceCents,
    quantity: normalizedQuantity,
    totalPriceCents,
    unitLabel: formatIndividualKeycapAmount(unitPriceCents),
    totalLabel: formatIndividualKeycapAmount(totalPriceCents),
  };
}

function readIndividualKeycapVariants(mount) {
  const source = mount.querySelector('[data-individual-keycap-variants]');
  if (!(source instanceof HTMLScriptElement)) return [];
  try {
    const variants = JSON.parse(source.textContent || '[]');
    return Array.isArray(variants) ? variants : [];
  } catch (_error) {
    return [];
  }
}

function resolveIndividualKeycapPriceTarget(mount) {
  const priceId = String(mount.dataset.productPriceId || '').trim();
  if (!priceId) return null;
  return document.getElementById(priceId);
}

function syncIndividualKeycapPrice(priceRoot, priceCents, available, selectedPriceError = false) {
  if (!(priceRoot instanceof HTMLElement)) return false;
  const amount = priceRoot.matches('.individual-product-price')
    ? priceRoot
    : priceRoot.querySelector('.price__regular .price-item--regular, .price-item--regular');
  if (!(amount instanceof HTMLElement)) return false;
  if (!amount.dataset.wired808OriginalPrice) amount.dataset.wired808OriginalPrice = amount.textContent || '';
  if (selectedPriceError || (priceCents !== null && !available)) {
    amount.textContent = 'PRICE SETUP UNAVAILABLE';
    delete priceRoot.dataset.wired808IndividualPriceCents;
    return false;
  }
  if (priceCents === null) {
    amount.textContent = amount.dataset.wired808OriginalPrice;
    delete priceRoot.dataset.wired808IndividualPriceCents;
    return true;
  }
  amount.textContent = formatIndividualKeycapPrice(priceCents);
  priceRoot.dataset.wired808IndividualPriceCents = String(priceCents);
  return true;
}

export function resolveCatalogAssetUrl(assetPath, catalogUrl) {
  const value = String(assetPath || '').trim();
  if (!value) return '';
  if (/^https:\/\//i.test(value)) return value;
  if (!value.startsWith('/') || value.startsWith('//')) return '';
  if (!catalogUrl) return value;
  try {
    const base = new URL(catalogUrl, globalThis.location?.href || 'https://wired808.com/');
    if (!['http:', 'https:'].includes(base.protocol)) return '';
    if (base.pathname.startsWith('/assets/catalog/')) return `${base.origin}/assets${value}`;
    return `${base.origin}${value}`;
  } catch (_error) {
    return '';
  }
}

function propertyInput(name, value, formId) {
  return `<input type="hidden" name="properties[${escapeHtml(name)}]" value="${escapeHtml(value)}" form="${escapeHtml(formId)}" />`;
}

function keycapCard(cap, selected, catalogUrl) {
  const image = resolveCatalogAssetUrl(cap.image, catalogUrl);
  return `<button class="individual-keycap-card ${selected ? 'selected' : ''}" type="button" data-individual-keycap-id="${escapeHtml(cap.id)}" aria-pressed="${selected}" aria-label="Choose ${escapeHtml(cap.label)}">
    <span class="individual-keycap-card-image"><img src="${escapeHtml(image)}" alt="" loading="lazy" decoding="async" /></span>
    <span class="individual-keycap-card-copy"><strong>${escapeHtml(cap.label)}</strong></span>
  </button>`;
}

function individualKeycapCategorySection(category, label, keycaps, selectedId, catalogUrl, open) {
  const cards = keycaps.map((cap) => keycapCard(cap, cap.id === selectedId, catalogUrl)).join('');
  return `<section class="individual-keycap-category-section ${open ? 'open' : 'closed'}" data-individual-keycap-category-section="${escapeHtml(category)}">
    <button id="individual-keycap-category-header-${escapeHtml(category)}" class="individual-keycap-category-header" type="button" data-individual-keycap-section-toggle="${escapeHtml(category)}" aria-expanded="${open}" aria-controls="individual-keycap-category-content-${escapeHtml(category)}">
      <span class="individual-keycap-category-title"><span class="individual-keycap-category-light" aria-hidden="true"></span><span class="individual-keycap-category-name">${escapeHtml(label)}</span></span>
      <span class="individual-keycap-category-leader" aria-hidden="true"></span>
      <span class="individual-keycap-category-toggle-mark" aria-hidden="true"></span>
    </button>
    <div id="individual-keycap-category-content-${escapeHtml(category)}" class="individual-keycap-category-content" role="region" aria-labelledby="individual-keycap-category-header-${escapeHtml(category)}" aria-hidden="${!open}"${open ? '' : ' inert'}>
      <div class="individual-keycap-category-content-inner"><div class="individual-keycap-grid">${cards}</div></div>
    </div>
  </section>`;
}

function syncIndividualKeycapDisclosure(sectionId, open) {
  const section = document.querySelector(`[data-individual-keycap-category-section="${CSS.escape(sectionId)}"]`);
  const header = section?.querySelector('[data-individual-keycap-section-toggle]');
  const content = section?.querySelector('.individual-keycap-category-content');
  if (!(section instanceof HTMLElement) || !(header instanceof HTMLButtonElement) || !(content instanceof HTMLElement)) return;
  section.classList.toggle('open', open);
  section.classList.toggle('closed', !open);
  header.setAttribute('aria-expanded', String(open));
  content.setAttribute('aria-hidden', String(!open));
  content.inert = !open;
}

function ensureNoindex() {
  let robots = document.querySelector('meta[name="robots"]');
  if (!(robots instanceof HTMLMetaElement)) {
    robots = document.createElement('meta');
    robots.name = 'robots';
    document.head.append(robots);
  }
  robots.content = 'noindex,nofollow';
}

function resolveProductForm(mount, requestedId) {
  if (requestedId) {
    const requested = document.getElementById(requestedId);
    if (requested instanceof HTMLFormElement) return requested;
  }
  const info = mount.closest('[id^="ProductInfo-"]') || mount.closest('.product__info-container');
  return info?.querySelector('form[action*="/cart/add"], product-form form') || document.querySelector('form[action*="/cart/add"]');
}

function prepareIndividualKeycapPurchaseBar(mount, productForm, formId, submitButton) {
  if (!(productForm instanceof HTMLFormElement) || !(submitButton instanceof HTMLButtonElement) || !formId) return null;
  const info = mount.closest('[id^="ProductInfo-"]') || mount.closest('.product__info-container') || mount.parentElement;
  const escapedFormId = globalThis.CSS?.escape ? CSS.escape(formId) : formId.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  const quantityControl = info?.querySelector(`input[name="quantity"][form="${escapedFormId}"], select[name="quantity"][form="${escapedFormId}"]`)
    || info?.querySelector('quantity-input input[name="quantity"], input[name="quantity"], select[name="quantity"]')
    || productForm.querySelector('input[name="quantity"], select[name="quantity"]');
  if (!(quantityControl instanceof HTMLInputElement) && !(quantityControl instanceof HTMLSelectElement)) return null;
  if (quantityControl instanceof HTMLSelectElement) {
    const option = [...quantityControl.options].find(item => item.value === '10+');
    if (option) {
      option.value = '10';
      option.textContent = '10';
    }
    const values = [...quantityControl.options].map(item => item.value);
    if (values.length !== 10 || values.some((value, index) => value !== String(index + 1))) return null;
  }
  const nativeQuantityRoot = quantityControl.closest('.product-form__quantity')
    || quantityControl.closest('.individual-quantity-label')
    || quantityControl.closest('quantity-input')
    || quantityControl;
  const nativeSubmitRoot = productForm.closest('product-form')
    || submitButton.closest('.product-form__buttons')
    || submitButton;
  if (!(nativeQuantityRoot instanceof HTMLElement) || !(nativeSubmitRoot instanceof HTMLElement)) return null;

  const purchaseBar = document.createElement('aside');
  purchaseBar.className = 'individual-keycap-buy-bar';
  purchaseBar.dataset.individualKeycapBuyBar = '';
  purchaseBar.setAttribute('aria-label', 'Selected keycap purchase');
  purchaseBar.hidden = true;
  purchaseBar.innerHTML = `
    <div class="individual-keycap-buy-bar-inner">
      <div class="individual-keycap-buy-bar-identity">
        <span class="individual-keycap-buy-bar-image"><img data-individual-keycap-buy-bar-image alt="" /></span>
        <span class="individual-keycap-buy-bar-copy"><strong data-individual-keycap-buy-bar-name></strong><span data-individual-keycap-buy-bar-unit-price></span></span>
      </div>
      <div class="individual-keycap-native-quantity-slot" data-individual-keycap-native-quantity-slot></div>
      <div class="individual-keycap-buy-bar-total"><span>Total</span><strong data-individual-keycap-buy-bar-total aria-live="polite"></strong></div>
      <div class="individual-keycap-native-submit-slot" data-individual-keycap-native-submit-slot></div>
    </div>`;
  purchaseBar.dataset.productFormId = formId;
  document.body.append(purchaseBar);

  const quantitySlot = purchaseBar.querySelector('[data-individual-keycap-native-quantity-slot]');
  const submitSlot = purchaseBar.querySelector('[data-individual-keycap-native-submit-slot]');
  if (!(quantitySlot instanceof HTMLElement) || !(submitSlot instanceof HTMLElement)) return null;
  nativeQuantityRoot.classList.add('individual-keycap-native-quantity-root');
  nativeSubmitRoot.classList.add('individual-keycap-native-submit-root');
  quantitySlot.append(nativeQuantityRoot);
  submitSlot.append(nativeSubmitRoot);
  productForm.classList.add('wired808-individual-native-form');

  quantityControl.setAttribute('form', formId);
  if (quantityControl instanceof HTMLInputElement) {
    quantityControl.setAttribute('min', '1');
    quantityControl.setAttribute('max', '10');
    quantityControl.setAttribute('step', '1');
  }
  if (!quantityControl.hasAttribute('aria-label') && !quantityControl.hasAttribute('aria-labelledby')) {
    quantityControl.setAttribute('aria-label', 'Quantity');
  }
  quantityControl.value = String(normalizeIndividualKeycapQuantity(quantityControl.value));
  submitButton.setAttribute('form', formId);

  if (quantityControl instanceof HTMLSelectElement) {
    let stepper = quantityControl.closest('.custom-quantity-dropdown');
    if (!(stepper instanceof HTMLElement)) {
      stepper = document.createElement('div');
      quantityControl.before(stepper);
      stepper.append(quantityControl);
    }
    stepper.classList.add('individual-keycap-quantity-stepper');
    for (const [name, label, symbol] of [['minus', 'Decrease quantity', '−'], ['plus', 'Increase quantity', '+']]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.name = name;
      button.dataset.individualKeycapQuantityStep = name === 'minus' ? '-1' : '1';
      button.setAttribute('aria-label', label);
      button.textContent = symbol;
      if (name === 'minus') stepper.prepend(button);
      else stepper.append(button);
    }
    nativeQuantityRoot.querySelector('.custom-quantity-input-wrapper')?.setAttribute('hidden', '');
  }

  const image = purchaseBar.querySelector('[data-individual-keycap-buy-bar-image]');
  const name = purchaseBar.querySelector('[data-individual-keycap-buy-bar-name]');
  const unitPrice = purchaseBar.querySelector('[data-individual-keycap-buy-bar-unit-price]');
  const total = purchaseBar.querySelector('[data-individual-keycap-buy-bar-total]');
  if (!(image instanceof HTMLImageElement) || !(name instanceof HTMLElement) || !(unitPrice instanceof HTMLElement) || !(total instanceof HTMLElement)) return null;

  let activePriceCents = null;
  const syncPurchaseBarTotal = () => {
    if (!Number.isSafeInteger(activePriceCents)) return;
    const summary = individualKeycapPurchaseSummary(activePriceCents, quantityControl.value);
    if (quantityControl.value !== String(summary.quantity)) quantityControl.value = String(summary.quantity);
    unitPrice.textContent = `${summary.unitLabel} each`;
    total.textContent = summary.totalLabel;
    nativeQuantityRoot.querySelectorAll('button').forEach((button) => {
      const decrement = button.matches('[name="minus"], [data-quantity-step="-1"]');
      const increment = button.matches('[name="plus"], [data-quantity-step="1"]');
      if (decrement) button.disabled = summary.quantity <= 1;
      if (increment) button.disabled = summary.quantity >= 10;
    });
  };
  quantityControl.addEventListener('input', syncPurchaseBarTotal);
  quantityControl.addEventListener('change', syncPurchaseBarTotal);
  nativeQuantityRoot.querySelectorAll('button').forEach((button) => {
    const decrement = button.matches('[name="minus"], [data-quantity-step="-1"]');
    const increment = button.matches('[name="plus"], [data-quantity-step="1"]');
    if (quantityControl instanceof HTMLSelectElement && (decrement || increment)) {
      button.addEventListener('click', () => {
        const delta = decrement ? -1 : 1;
        quantityControl.value = String(normalizeIndividualKeycapQuantity(Number(quantityControl.value) + delta));
        quantityControl.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } else {
      button.addEventListener('click', () => setTimeout(syncPurchaseBarTotal, 0));
    }
  });

  const syncSelection = (selected, priceCents, purchaseReady, catalogUrl) => {
    purchaseBar.hidden = !purchaseReady;
    document.body.classList.toggle('wired808-individual-buy-bar-visible', purchaseReady);
    if (!purchaseReady || !selected) {
      activePriceCents = null;
      return;
    }
    activePriceCents = priceCents;
    image.src = resolveCatalogAssetUrl(selected.image, catalogUrl);
    image.alt = '';
    name.textContent = selected.label.toUpperCase();
    syncPurchaseBarTotal();
  };

  return { purchaseBar, quantityControl, syncSelection, syncPurchaseBarTotal };
}

function individualKeycapGalleryPhotos(mount) {
  const data = mount.dataset;
  return [
    {
      src: data.galleryMainUrl || '/assets/individual-keycaps-gallery/choose-your-keycaps-hawaiian-grid--sha256-6961d5113b14223ec5db9bc5743f11112830655d195ac3a419f813adb7a7fec0.jpeg',
      alt: 'Nine-panel Choose Your Keycaps grid of Hawaiian shell and quilt designs',
      width: 1320,
      height: 1643,
    },
    {
      src: data.gallerySecondUrl || '/assets/individual-keycaps-gallery/tropical-keycaps.webp',
      alt: 'Decorative keycaps arranged on wet tropical rock',
      width: 1080,
      height: 1350,
    },
    {
      src: data.galleryThirdUrl || '/assets/individual-keycaps-gallery/pokemon-keycaps.webp',
      alt: 'Pokémon keycap collection beside a mechanical keyboard',
      width: 941,
      height: 1672,
    },
  ];
}

function galleryMediaRailButtons(galleryPhotos, activeIndex) {
  return galleryPhotos.map((photo, index) => `<button type="button" class="wired808-individual-media-thumb ${index === activeIndex ? 'active' : ''}" data-individual-keycap-media-thumb="${index}" aria-pressed="${index === activeIndex}" aria-label="Show photo ${index + 1} of ${galleryPhotos.length}">
      <img src="${escapeHtml(photo.src)}" width="${photo.width}" height="${photo.height}" alt="" loading="lazy" decoding="async" />
    </button>`).join('');
}

function syncGalleryMediaRail(galleryPhotos, activeIndex) {
  const rail = document.querySelector('[data-wired808-individual-media-thumbs]');
  if (!(rail instanceof HTMLElement)) return;
  rail.dataset.activePhoto = String(activeIndex);
  rail.innerHTML = galleryMediaRailButtons(galleryPhotos, activeIndex);
  const hero = document.querySelector('[data-individual-keycap-main-image]');
  const activePhoto = galleryPhotos[activeIndex];
  if (hero instanceof HTMLImageElement && activePhoto) {
    hero.src = activePhoto.src;
    hero.alt = activePhoto.alt;
  }
}

function activeGalleryPhotoIndex(rail) {
  const value = Number(rail?.dataset?.activePhoto);
  return Number.isInteger(value) && value >= 0 ? value : 0;
}

function bindGalleryMediaRail(rail, galleryPhotos) {
  if (!(rail instanceof HTMLElement) || rail.dataset.wired808GalleryBound === 'true') return;
  rail.dataset.wired808GalleryBound = 'true';
  rail.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const thumb = target?.closest('[data-individual-keycap-media-thumb]');
    if (!(thumb instanceof HTMLElement)) return;
    const nextIndex = Number(thumb.dataset.individualKeycapMediaThumb);
    if (Number.isInteger(nextIndex) && nextIndex >= 0 && nextIndex < galleryPhotos.length && nextIndex !== activeGalleryPhotoIndex(rail)) {
      syncGalleryMediaRail(galleryPhotos, nextIndex);
    }
  });
}

function prepareNativeProductShell(mount) {
  ensureNoindex();
  document.documentElement.classList.add('wired808-individual-keycaps-page');
  document.body.classList.add('wired808-individual-keycaps-page');
  const info = mount.closest('[id^="ProductInfo-"]') || mount.closest('.product__info-container');
  const product = info?.closest('.product') || document.querySelector('.product');
  const title = info?.querySelector('.product__title h1, .product__title h2, h1');
  if (title) title.textContent = 'Individual Keycaps';
  if (document.title) document.title = document.title.replace(/Extra Keycap Add-on/gi, 'Individual Keycaps');
  if (!product) return null;

  product.classList.remove('product--no-media');
  product.classList.add('grid--2-col-tablet', 'wired808-individual-product');
  const mediaWrapper = product.querySelector('.product__media-wrapper');
  if (!mediaWrapper) return null;

  const galleryPhotos = individualKeycapGalleryPhotos(mount);
  const existingRail = mediaWrapper.nextElementSibling;
  if (existingRail instanceof HTMLElement && existingRail.hasAttribute('data-wired808-individual-media-thumbs')) {
    bindGalleryMediaRail(existingRail, galleryPhotos);
    syncGalleryMediaRail(galleryPhotos, activeGalleryPhotoIndex(existingRail));
    return mediaWrapper.querySelector('[data-wired808-individual-media]');
  }
  mediaWrapper.innerHTML = `<div class="wired808-individual-media" data-wired808-individual-media role="list" aria-label="Product images">
    ${galleryPhotos.map((photo, index) => `<figure class="wired808-individual-media-stage" data-individual-keycap-media-item role="listitem">
      <img ${index === 0 ? 'data-individual-keycap-main-image ' : ''}src="${escapeHtml(photo.src)}" width="${photo.width}" height="${photo.height}" alt="${escapeHtml(photo.alt)}" loading="${index === 0 ? 'eager' : 'lazy'}" decoding="async" />
    </figure>`).join('')}
  </div>`;
  const rail = document.createElement('div');
  rail.className = 'wired808-individual-media-thumbs';
  rail.dataset.wired808IndividualMediaThumbs = '';
  rail.dataset.activePhoto = '0';
  rail.setAttribute('role', 'group');
  rail.setAttribute('aria-label', 'Choose photo');
  rail.innerHTML = galleryMediaRailButtons(galleryPhotos, 0);
  bindGalleryMediaRail(rail, galleryPhotos);
  mediaWrapper.after(rail);
  return mediaWrapper.querySelector('[data-wired808-individual-media]');
}

export async function mountIndividualKeycapsPicker(mount) {
  if (!mount) return null;
  const catalogUrl = mount.dataset.catalogUrl || globalThis.WIRED808_CATALOG_URL || '/src/catalog.json';
  const requestedFormId = mount.dataset.productFormId || '';
  const previewOnly = mount.dataset.previewOnly === 'true';
  const carrierVariants = readIndividualKeycapVariants(mount);
  const state = { search: '', selectedId: '', selectedVariantId: '', ready: false, openCategories: ['animals'] };
  const productForm = resolveProductForm(mount, requestedFormId);
  const formId = productForm?.getAttribute('id') || requestedFormId;
  const variantInput = productForm?.querySelector('input[name="id"]') || null;
  const priceRoot = resolveIndividualKeycapPriceTarget(mount);
  const submitButton = productForm?.querySelector('[type="submit"]') || null;
  if (productForm && !productForm.getAttribute('id') && formId) productForm.setAttribute('id', formId);
  if (productForm) productForm.dataset.wired808IndividualReady = 'false';
  if (submitButton && 'disabled' in submitButton) {
    submitButton.disabled = true;
    submitButton.setAttribute('aria-disabled', 'true');
  }
  const purchaseControls = prepareIndividualKeycapPurchaseBar(mount, productForm, formId, submitButton);
  const purchaseBar = purchaseControls?.purchaseBar || null;

  const guardRequiredSelection = (event) => {
    if (!previewOnly && state.ready) {
      purchaseControls?.syncPurchaseBarTotal();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  };
  productForm?.addEventListener('submit', guardRequiredSelection, true);

  mount.innerHTML = '';
  mount.setAttribute('aria-busy', 'true');
  let catalog;
  try {
    const response = await fetch(catalogUrl, { cache: 'no-store' });
    if (!response.ok) throw new Error(`catalog request returned ${response.status}`);
    catalog = await response.json();
  } catch (error) {
    mount.innerHTML = `<div class="individual-keycap-load-error" role="alert" data-individual-keycap-status tabindex="-1"><strong>Keycaps could not load.</strong><span>Please refresh and try again.</span></div>`;
    mount.setAttribute('aria-busy', 'false');
    console.warn('Wired808 Individual Keycaps catalog load failed', error);
    return null;
  }

  const keycaps = filterShopperVisibleKeycaps(catalog.keycaps);
  const byId = new Map(keycaps.map((cap) => [cap.id, cap]));
  const categoryModel = individualKeycapCategorySections(catalog, keycaps);
  if (!categoryModel.some((category) => state.openCategories.includes(category.id))) {
    state.openCategories = categoryModel[0] ? [categoryModel[0].id] : [];
  }
  const galleryPhotos = individualKeycapGalleryPhotos(mount);
  bindGalleryMediaRail(document.querySelector('[data-wired808-individual-media-thumbs]'), galleryPhotos);
  prepareNativeProductShell(mount);

  const render = ({ preserveSearchFocus = false, cursor = null, preserveCategoryScroll = false } = {}) => {
    const previousCategoryScroll = preserveCategoryScroll ? Number(mount.querySelector('.individual-keycap-category-sections')?.scrollTop || 0) : 0;
    const filtered = sortIndividualKeycaps(filterIndividualKeycaps(keycaps, { search: state.search }));
    const selected = byId.get(state.selectedId) || null;
    const properties = selected ? individualKeycapProperties(selected) : null;
    let selectedPriceCents = null;
    let selectedPriceError = false;
    let variant = null;
    if (selected) {
      try {
        selectedPriceCents = individualKeycapPriceCents(selected);
        variant = resolveIndividualKeycapVariant(carrierVariants, selectedPriceCents);
      } catch (_error) {
        selectedPriceCents = null;
        selectedPriceError = true;
      }
    }
    state.selectedVariantId = variant?.id || '';
    const searching = Boolean(state.search.trim());
    const categorySections = categoryModel.map(({ id: category, label }) => {
      const categoryKeycaps = filtered.filter((cap) => normalizeKeycapCategory(keycapCategoryValue(cap)) === category);
      if (!categoryKeycaps.length) return '';
      const open = searching || state.openCategories.includes(category);
      return individualKeycapCategorySection(category, label, categoryKeycaps, state.selectedId, catalogUrl, open);
    }).join('');
    const categoryContent = categorySections || `<div class="individual-keycap-empty" role="status"><strong>No keycaps found</strong><span>Try another search.</span><button type="button" data-individual-keycap-clear>Clear search</button></div>`;

    mount.innerHTML = `<section class="individual-keycaps-picker" aria-label="Keycap designs" data-individual-keycaps-results="${filtered.length}" data-selected-keycap-id="${escapeHtml(state.selectedId)}">
      <label class="individual-keycap-search">
        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.8" cy="10.8" r="6.2"></circle><path d="m16 16 4.2 4.2"></path></svg>
        <input type="search" data-individual-keycap-search aria-label="Search ${keycaps.length} individual keycap designs" value="${escapeHtml(state.search)}" placeholder="Search ${keycaps.length} keycaps" autocomplete="off" spellcheck="false" />
      </label>
      <div class="individual-keycap-results-bar" aria-live="polite"><strong>${filtered.length}</strong><span>${filtered.length === 1 ? 'design' : 'designs'}</span>${searching ? '<button type="button" data-individual-keycap-clear>Clear search</button>' : ''}</div>
      <div class="individual-keycap-category-sections" aria-label="Keycap categories">${categoryContent}</div>
      <div class="individual-keycap-form-properties" hidden>${properties && formId ? Object.entries(properties).map(([name, value]) => propertyInput(name, value, formId)).join('') : ''}</div>
    </section>`;

    mount.dataset.results = String(filtered.length);
    mount.dataset.selectedKeycapId = state.selectedId;
    mount.dataset.selectedPriceCents = selectedPriceCents === null ? '' : String(selectedPriceCents);
    mount.setAttribute('aria-busy', 'false');
    const priceSynchronized = syncIndividualKeycapPrice(priceRoot, selected ? selectedPriceCents : null, !selected || Boolean(variant), selectedPriceError);
    if (variantInput instanceof HTMLInputElement && variant && variantInput.value !== variant.id) {
      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const purchaseReady = Boolean(selected && variant && priceSynchronized && purchaseBar);
    const ready = Boolean(!previewOnly && purchaseReady);
    state.ready = ready;
    if (productForm) productForm.dataset.wired808IndividualReady = ready ? 'true' : 'false';
    if (submitButton && 'disabled' in submitButton) {
      submitButton.disabled = !ready;
      submitButton.setAttribute('aria-disabled', String(submitButton.disabled));
    }
    purchaseControls?.syncSelection(selected, selectedPriceCents, purchaseReady, catalogUrl);

    const categoryContainer = mount.querySelector('.individual-keycap-category-sections');
    if (categoryContainer && preserveCategoryScroll) categoryContainer.scrollTop = previousCategoryScroll;
    if (preserveSearchFocus) {
      const input = mount.querySelector('[data-individual-keycap-search]');
      if (input instanceof HTMLInputElement) {
        input.focus({ preventScroll: true });
        const nextCursor = cursor ?? input.value.length;
        try { input.setSelectionRange(nextCursor, nextCursor); } catch (_error) {}
      }
    }
  };

  const selectKeycap = (id, options = {}) => {
    if (!byId.has(id)) return;
    state.selectedId = id;
    const category = normalizeKeycapCategory(keycapCategoryValue(byId.get(id)));
    if (!state.openCategories.includes(category)) state.openCategories = [...state.openCategories, category];
    render({ preserveCategoryScroll: options.preserveCategoryScroll !== false });
    mount.querySelector(`[data-individual-keycap-id="${CSS.escape(id)}"]`)?.focus({ preventScroll: true });
  };

  mount.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const card = target?.closest('[data-individual-keycap-id]');
    if (card instanceof HTMLElement) {
      selectKeycap(card.dataset.individualKeycapId || '');
      return;
    }
    const toggle = target?.closest('[data-individual-keycap-section-toggle]');
    if (toggle instanceof HTMLElement) {
      const sectionId = toggle.dataset.individualKeycapSectionToggle || '';
      if (!sectionId) return;
      const section = toggle.closest('[data-individual-keycap-category-section]');
      const currentlyOpen = section?.classList.contains('open') ?? toggle.getAttribute('aria-expanded') === 'true';
      const { open, openCategories } = nextIndividualKeycapDisclosureState(state.openCategories, sectionId, currentlyOpen);
      state.openCategories = openCategories;
      syncIndividualKeycapDisclosure(sectionId, open);
      return;
    }
    if (target?.closest('[data-individual-keycap-clear]')) {
      state.search = '';
      render({ preserveSearchFocus: true });
    }
  });

  mount.addEventListener('input', (event) => {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    if (!input?.matches('[data-individual-keycap-search]')) return;
    const cursor = input.selectionStart ?? input.value.length;
    state.search = input.value;
    render({ preserveSearchFocus: true, cursor });
  });

  render();
  return { getState: () => ({ ...state }), keycaps };
}

const INDIVIDUAL_FAQ_LABELS = ['SHIPPING', 'RETURNS', 'CUSTOM ORDERS', 'CONTACT US'];
const individualFaqClosingTimers = new WeakMap();

function individualFaqLabel(summary) {
  return String(summary?.querySelector('.accordion__title')?.textContent || summary?.textContent || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

export function individualFaqMatchesApprovedLabels(labels) {
  return Array.isArray(labels)
    && labels.length === INDIVIDUAL_FAQ_LABELS.length
    && INDIVIDUAL_FAQ_LABELS.every((label, index) => labels[index] === label);
}

function setIndividualFaqExpanded(details, open) {
  const summary = details.querySelector('summary');
  if (summary) summary.setAttribute('aria-expanded', open ? 'true' : 'false');
}

function clearIndividualFaqClose(details) {
  const timer = individualFaqClosingTimers.get(details);
  if (timer) clearTimeout(timer);
  individualFaqClosingTimers.delete(details);
}

function individualFaqPrefersReducedMotion(node) {
  const view = node?.ownerDocument?.defaultView;
  return Boolean(view && typeof view.matchMedia === 'function'
    && view.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function closeIndividualFaqItem(details) {
  clearIndividualFaqClose(details);
  setIndividualFaqExpanded(details, false);
  if (!details.open) {
    details.classList.remove('is-closing');
    return;
  }
  const prefersReducedMotion = individualFaqPrefersReducedMotion(details);
  if (prefersReducedMotion) {
    details.open = false;
    details.classList.remove('is-closing', 'is-opening');
    return;
  }
  details.classList.add('is-closing');
  const timer = setTimeout(() => {
    details.open = false;
    details.classList.remove('is-closing');
    individualFaqClosingTimers.delete(details);
  }, 500);
  individualFaqClosingTimers.set(details, timer);
}

function openIndividualFaqItem(details, allDetails) {
  for (const otherDetails of allDetails) {
    if (otherDetails !== details && otherDetails.open) closeIndividualFaqItem(otherDetails);
  }
  clearIndividualFaqClose(details);
  details.classList.remove('is-closing');
  if (individualFaqPrefersReducedMotion(details)) {
    details.classList.remove('is-opening');
    details.open = true;
    setIndividualFaqExpanded(details, true);
    return;
  }
  details.classList.add('is-opening');
  details.open = true;
  setIndividualFaqExpanded(details, true);
  requestAnimationFrame(() => requestAnimationFrame(() => details.classList.remove('is-opening')));
}

function revealIndividualFaqItems(section, items) {
  const reveal = () => {
    requestAnimationFrame(() => items.forEach((item) => item.classList.add('is-visible')));
  };
  const view = section.ownerDocument?.defaultView;
  if (view && typeof view.matchMedia === 'function'
    && view.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    items.forEach((item) => item.classList.add('is-visible'));
    return;
  }
  if (typeof IntersectionObserver !== 'function') {
    reveal();
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    reveal();
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  observer.observe(section);
}

export function enhanceIndividualFaq(root = document) {
  const sections = [...root.querySelectorAll('.collapsible-content')];
  const section = sections.find((candidate) => {
    const labels = [...candidate.querySelectorAll('details > summary')].map(individualFaqLabel);
    return individualFaqMatchesApprovedLabels(labels);
  });
  if (!section || section.dataset.wired808IndividualFaqEnhanced === 'true') return 0;

  const detailsItems = [...section.querySelectorAll('details')];
  const faqRows = detailsItems.map((details) => ({
    details,
    summary: details.querySelector('summary'),
    title: details.querySelector('summary .accordion__title'),
    content: details.querySelector('.accordion__content'),
    accordion: details.closest('.accordion'),
  }));
  if (faqRows.some(({ summary, title, content, accordion }) => !summary || !title || !content || !accordion)) return 0;
  const accordionItems = faqRows.map(({ accordion }) => accordion);
  if (new Set(accordionItems).size !== detailsItems.length) return 0;
  const grid = accordionItems[0]?.parentElement;
  if (!grid || !accordionItems.every((item) => item.parentElement === grid)) return 0;

  let initialOpenFound = false;
  for (const details of detailsItems) {
    if (!details.open) continue;
    if (initialOpenFound) details.open = false;
    else initialOpenFound = true;
  }

  section.dataset.wired808IndividualFaqEnhanced = 'true';
  section.classList.add('wired808-individual-faq');
  section.closest('.shopify-section')?.classList.add('wired808-product-faq-section');
  grid.classList.add('wired808-individual-faq-grid');

  const ownerDocument = section.ownerDocument;
  const leftColumn = ownerDocument.createElement('div');
  const rightColumn = ownerDocument.createElement('div');
  leftColumn.className = 'wired808-individual-faq-column';
  rightColumn.className = 'wired808-individual-faq-column';

  accordionItems.forEach((item, index) => {
    item.classList.add('wired808-individual-faq-row');
    item.style.setProperty('--wired808-faq-order', String(index));
    item.style.setProperty('--wired808-faq-delay', `${(index + 1) * 150}ms`);
    (index < Math.ceil(accordionItems.length / 2) ? leftColumn : rightColumn).append(item);
  });
  grid.replaceChildren(leftColumn, rightColumn);

  faqRows.forEach(({ details, summary, title, content }, index) => {
    details.classList.add('wired808-individual-faq-item');
    const idBase = `wired808-individual-faq-${index + 1}`;
    if (!summary.id) summary.id = `${idBase}-summary`;
    if (!content.id) content.id = `${idBase}-answer`;
    summary.setAttribute('aria-controls', content.id);
    content.setAttribute('role', 'region');
    content.setAttribute('aria-labelledby', summary.id);
    title.style.setProperty('font-size', '12px', 'important');
    summary.style.setProperty('min-height', 'var(--wired808-faq-summary-height)', 'important');
    summary.style.setProperty('padding', 'var(--wired808-faq-summary-padding)', 'important');
    setIndividualFaqExpanded(details, details.open);

    if (!summary.querySelector('.wired808-individual-faq-mark')) {
      const mark = ownerDocument.createElement('span');
      mark.className = 'wired808-individual-faq-mark';
      mark.setAttribute('aria-hidden', 'true');
      summary.append(mark);
    }

    if (!content.querySelector(':scope > .wired808-individual-faq-answer-inner')) {
      const inner = ownerDocument.createElement('div');
      inner.className = 'wired808-individual-faq-answer-inner';
      inner.append(...content.childNodes);
      content.append(inner);
    }
    for (const copy of content.querySelectorAll('p, a')) {
      copy.style.setProperty('font-family', 'Arial, Helvetica, sans-serif', 'important');
      copy.style.setProperty('font-size', '15.2px', 'important');
      copy.style.setProperty('font-weight', '400', 'important');
      copy.style.setProperty('line-height', '23.75px', 'important');
      copy.style.setProperty('letter-spacing', '0', 'important');
      copy.style.setProperty('text-transform', 'none', 'important');
    }

    summary.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      const isOpen = details.open && !details.classList.contains('is-closing');
      if (isOpen) closeIndividualFaqItem(details);
      else openIndividualFaqItem(details, detailsItems);
    }, true);
  });

  revealIndividualFaqItems(section, accordionItems);
  return detailsItems.length;
}

if (typeof document !== 'undefined') {
  enhanceIndividualFaq(document);
  document.querySelectorAll('[data-wired808-individual-keycaps]').forEach((mount) => {
    void mountIndividualKeycapsPicker(mount);
  });
}
